// Class strings the catalog and import pages share for small pieces of markup. They are plain Tailwind utilities with semantic tokens (the
// kit rules), kept in one place so a list, a hint or a heading looks the same on every page of the two areas.

/** A line of help or a quiet fact under a heading or beside a control. */
export const hintText = "m-0 text-sm leading-5 text-muted-foreground";

/** A heading inside a card (the card title is the `h2`). */
export const subheading = "m-0 text-base font-semibold leading-6 text-foreground";

/** A list without bullets, with space between the rows. */
export const plainList = "m-0 grid list-none gap-2 p-0";

/** One bordered row of such a list (a note, a history event, a chosen item). */
export const listRow = "rounded-md border border-border px-3 py-2";

/** A definition list of labelled facts: the labels are small and quiet, the values are plain. */
export const factList =
  "m-0 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(10rem,1fr))] [&_dd]:m-0 [&_dt]:text-xs [&_dt]:text-muted-foreground";

/** A block inside a card that is set off by a rule above it. */
export const subsection = "grid gap-4 border-t border-border pt-4";

/** A wrapping row of controls (the buttons under a form). */
export const actionRow = "flex flex-wrap items-center gap-2";

/** A grid of fields that wraps down to one column. Inside a parent with `data-fields="two"` it is exactly two columns from the sm size up (the new-item form). */
export const fieldGrid =
  "grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,15rem),1fr))] in-data-[fields=two]:sm:grid-cols-2";

/** One chosen entry in an editable list (a category, a person): its name takes the free space, its controls wrap under it. */
export const chosenRow = `${listRow} flex flex-wrap items-center justify-between gap-2`;

/** The name inside a chosen row. */
export const chosenName = "min-w-40 flex-1 font-semibold";

/** The small heading that names a group inside a section (an `h3` under the card title, an `h4` under a section heading). */
export const groupHeading = "m-0 text-sm font-semibold text-muted-foreground";

/** A link shaped like a pill, for a row of jump links or tabs. The current one (`aria-current`) has a heavier border as well as a fill. */
export const pillLink =
  "inline-flex min-h-11 items-center rounded-full border-2 border-transparent bg-pill px-3.5 text-sm font-medium text-foreground no-underline hover:bg-border compact:min-h-8 aria-[current=page]:border-primary aria-[current=page]:bg-accent aria-[current=page]:font-semibold aria-[current=page]:text-link";

/** The box around a list filter form. */
export const filterPanel = "grid gap-4 rounded-lg border border-hairline bg-card p-5";

/** A bordered group of related controls under a legend (the kit `FieldGroup`, for a fieldset that needs its own attributes). */
export const groupBox =
  "m-0 grid min-w-0 content-start gap-2 rounded-md border border-border p-4 [&>legend+*]:clear-left";

/** The legend of such a group. */
export const groupLegend = "float-left w-full p-0 text-[15px] font-semibold";

/** The margin that lets a 44 px (comfortable) or 32 px (compact) control sit in a table cell without making the row taller. */
export const inRowTarget = "-my-2 compact:-my-1";

/** A bordered block under a results list (the reasons, the blocking filters, the debug summary). */
export const panelBox = "grid gap-2 rounded-md border border-border bg-card p-4";

/** A definition list of scores: a grid of small labels over tabular numbers. */
export const scoreList =
  "m-0 grid gap-x-4 gap-y-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,11.25rem),1fr))] [&_dd]:m-0 [&_dd]:tabular-nums [&_dd]:[overflow-wrap:anywhere] [&_dt]:text-[13px] [&_dt]:text-muted-foreground [&>div]:min-w-0";

/** A bulleted list inside a results block. */
export const bulletList = "m-0 list-disc pl-5";

/** One checkbox and its label in a row, as tall as a target. The kit `CheckboxField` has the same shape; use this one when the box needs its own attributes. */
export const checkRow =
  "grid min-h-11 min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8";

/** The label of such a row. */
export const checkLabel = "flex min-h-11 items-center font-normal compact:min-h-8";

/** The text of a field error written by hand (the kit fields write their own). */
export const fieldError = "m-0 text-sm leading-5 font-semibold text-destructive";

/** The track of a row of views of one list (a segmented control made of links). */
export const segmentTrack =
  "m-0 flex w-fit max-w-full list-none flex-wrap gap-1 rounded-md bg-muted p-[3px] compact:gap-2";

/** One link of that track. The current one has a card fill, a ring and a heavier word, so it is not shown by colour alone. */
export const segmentLink =
  "inline-flex min-h-11 items-center rounded-[9px] px-3.5 text-sm font-medium text-muted-foreground no-underline hover:text-foreground compact:min-h-8 aria-[current=page]:bg-card aria-[current=page]:font-semibold aria-[current=page]:text-foreground aria-[current=page]:ring-1 aria-[current=page]:ring-border";
